import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Modal } from "./Modal";
import { Button } from "@/components/ui/button";
import { useLogout } from "@/hooks/useLogout";

type IProps = {
  open: boolean;
  title?: string;
  cancelText?: string;
  isLoading?: boolean;
  confirmText?: string;
  description?: string;
  onCancel: () => void;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  size?: "sm" | "md" | "lg" | "xl" | "full";
};

const LogoutWarningDialog = ({
  open,
  onCancel,
  onConfirm,
  title = "",
  size = "sm",
  onOpenChange,
  description = "",
  isLoading = false,
  cancelText,
  confirmText,
}: IProps) => {
  const { t } = useTranslation(["common"]);
  const { logoutUser } = useLogout();

  const cancelLabel = cancelText ?? t("common:buttons.cancel");
  const confirmLabel = confirmText ?? t("common:chrome.sign_out");

  const handleCloseClick = (open: boolean) => {
    if (!open) onCancel();
  };

  const handleConfirm = () => {
    onConfirm();
    logoutUser();
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const dialogData = {
    title,
    description,
    body: <LogoutDialogBody />,
    footer: (
      <div className="flex gap-3 w-full">
        <Button variant="outline" onClick={handleCancel} disabled={isLoading} className="grow">
          {cancelLabel}
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={isLoading} className="grow">
          {isLoading ? t("common:states.loading") : confirmLabel}
        </Button>
      </div>
    ),
  };

  return (
    <Modal
      open={open}
      size={size}
      dialogData={dialogData}
      onOpenChange={onOpenChange}
      onCloseClick={handleCloseClick}
      styles={{
        dialogFooter: "border-none",
      }}
    />
  );
};

const LogoutDialogBody = () => {
  const { t } = useTranslation(["common"]);

  return (
    <div className="flex flex-col gap-5 items-center">
      <div className="flex flex-col items-center gap-2">
        <div className="p-4 bg-rose-500/10 rounded-full">
          <Lock size={56} strokeWidth={1} className="text-danger" />
        </div>
      </div>
      <span>
        <p className="text-xl font-medium text-danger text-center">
          {t("common:logout_dialog.confirm_sign_out")}
        </p>
        <p className="text-center leading-none mt-1">
          {t("common:logout_dialog.are_you_sure_sign_out")}
        </p>
      </span>
    </div>
  );
};

export default LogoutWarningDialog;
